import { IMat4 } from "@base/math/Mat4";
import { IPointMountInfo } from "@biz/definitions/MountDefinitons";
import { SST } from "@biz/definitions/SST";
import { ISunSailData, PoleIndex, PoleType, SunSailType } from "@biz/definitions/SunSailDefinitions";
import { PoleLogic } from "@biz/items/sunSail/logic/PoleLogic";
import { SunSailLogic } from "@biz/items/sunSail/logic/SunSailLogic";
import { ISail2dForm } from "@biz/models/Sail2dDefinitions";
import { PD } from "@biz/PD";
import { ISCSunSailData } from "@biz/sc/sunSail/ISCSunSailData";

export interface IIndirectPylon {
  axlePylon: PoleIndex;
  oppositeAxlePylon: PoleIndex;
  indirectPylon: PoleIndex;
  oppositeIndirectPylon: PoleIndex;
}

export class IndirectPylonLogic {
  static isIndirectPylonPossible(sunSailType: SunSailType): boolean {
    return SST.is(sunSailType, SST.C);
  }

  static getIndirectPylons(sunSail: ISunSailData | ISCSunSailData | ISail2dForm): IIndirectPylon[] {
    const sunSailType = PD.productToSunSailType(sunSail)
    if (!IndirectPylonLogic.isIndirectPylonPossible(sunSailType))
      return [];
    const axlePylonCount = SunSailLogic.calcAxlePylonCount(sunSail);
    if (axlePylonCount === 0)
      return [];

    const ret: IIndirectPylon[] = [];
    if (SunSailLogic.getPolePylonType(sunSail, PoleIndex.A)) {
      const oppositeIndirectPylon = SunSailLogic.getPolePylonType(sunSail, PoleIndex.D) ? PoleIndex.D : PoleIndex.B;
      ret.push({
        axlePylon: PoleIndex.A,
        oppositeAxlePylon: PoleIndex.C,
        oppositeIndirectPylon,
        indirectPylon: oppositeIndirectPylon === PoleIndex.B ? PoleIndex.D : PoleIndex.B,
      })
    }

    if (SunSailLogic.getPolePylonType(sunSail, PoleIndex.C)) {
      const oppositeIndirectPylon = SunSailLogic.getPolePylonType(sunSail, PoleIndex.B) ? PoleIndex.B : PoleIndex.D;
      ret.push({
        axlePylon: PoleIndex.C,
        oppositeAxlePylon: PoleIndex.A,
        oppositeIndirectPylon,
        indirectPylon: oppositeIndirectPylon === PoleIndex.B ? PoleIndex.D : PoleIndex.B,
      })
    }
    return ret;
  }

  static isOppositeIndirectPylon(indirectPylonInfos: IIndirectPylon[], poleIndex: PoleIndex): boolean {
    for (const ipi of indirectPylonInfos) {
      if (ipi.oppositeIndirectPylon === poleIndex)
        return true;
    }
    return false;
  }

  static isOppositeAxlePylon(indirectPylonInfos: IIndirectPylon[], poleIndex: PoleIndex): boolean {
    for (const ipi of indirectPylonInfos) {
      if (ipi.oppositeAxlePylon === poleIndex)
        return true;
    }
    return false;
  }

  static calcTopPoleExtension(sunSail: ISunSailData | ISCSunSailData | ISail2dForm, poleIndex: number): number {
    const indirectPylonInfos = IndirectPylonLogic.getIndirectPylons(sunSail);
    for (const indirectPylonInfo of indirectPylonInfos) {
      if (poleIndex === indirectPylonInfo.oppositeAxlePylon || poleIndex === indirectPylonInfo.axlePylon)
        return 0.14;
      // old
      // if (poleIndex === indirectPylonInfo.oppositeAxlePylon) {
      //   return PoleLogic.isDroppole(sunSail.poles[poleIndex].material) ? 0.2 : 0.14;
      // } else if (poleIndex === indirectPylonInfo.axlePylon) {
      //   return PoleLogic.isDroppole(sunSail.poles[poleIndex].material) ? 0.2 : 0.14; // 0.13 for both?
      // }
    }
    return 0;
  }

  static calcAxlePylonRopeMountType(sunSail: ISunSailData, poleIndex: number): IPointMountInfo | undefined {
    if (!PoleLogic.isAxle(sunSail.sunSailType, poleIndex))
      throw new Error("Axle pole required")

    const indirectPylonInfos = IndirectPylonLogic.getIndirectPylons(sunSail);
    if (IndirectPylonLogic.isOppositeAxlePylon(indirectPylonInfos, poleIndex)) {
      return { mountType: "clamp", extendedMount: [{ type: "shackleOnly", articles: ["00E13000026"] }] };
    }

    const poleType = SunSailLogic.getPoleType(sunSail, poleIndex)
    const poleMaterial = SunSailLogic.getPoleMaterial(sunSail, poleIndex);
    const pylonType = SunSailLogic.getPolePylonType(sunSail, poleIndex);
    if (pylonType) {
      return { mountType: "pylon", extendedMount: [{ type: "direct" }] };
    }

    for (const indirect of indirectPylonInfos) {
      if (indirect.axlePylon === poleIndex)
        return { mountType: "pylon", extendedMount: [{ type: "direct" }] };
      if (indirect.oppositeAxlePylon === poleIndex) {
        if (poleType === PoleType.Wall) {
          return { mountType: "adapter_buegel", extendedMount: [{ type: "shackleOnly", articles: ["00E13000026"] }] };
        } else {
          if (PoleLogic.isAluOrSteel(poleMaterial))
            return { mountType: "bolt", extendedMount: [{ type: "direct" }] };
          return { mountType: "clamp", extendedMount: [{ type: "direct" }] };
        }
      }
    }

    return undefined;
  }

  static calcPylonRopeMatrix(sunSail: ISunSailData, poleIndex: number): IMat4 {
    return SunSailLogic.calcMatrixForPos(sunSail, poleIndex, -IndirectPylonLogic.calcTopPoleExtension(sunSail, poleIndex), "mount")
  }
}